import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import {
  EditTaskTypeDialog,
  TaskType as TaskTypeV2
} from '@sierralabs/workflow-ui';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import AppSnackbar from '../../common/AppSnackbar';
import { formatDate } from '../../common/formatter';
import SearchBox from '../../common/SearchBox';
import UserLink from '../users/components/UserLink';
import WorkflowApiLevelLabel from '../workflow/components/WorkflowApiLevelLabel';
import TaskTypeCreateDialog from './dialogs/TaskTypeCreateDialog';
import { useFindTaskTypeList } from './TaskType.hooks';

const StyledWorkflowApiLevelLabel = styled(WorkflowApiLevelLabel)`
  margin-left: 8px;
`;

const columns: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'label', label: 'Label' },
  { key: 'prefix', label: 'Prefix' },
  { key: 'entity', label: 'Target Entity' },
  { key: 'apiLevel', label: 'API Level' },
  { key: 'modified', label: 'Last Modified' },
  { key: 'modifiedBy', label: 'Modified By' }
];

const TaskTypeListPage: React.FC = () => {
  const history = useHistory();
  const [search, setSearch] = useQueryParam(
    'search',
    withDefault(StringParam, '')
  );
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam(
    'order',
    withDefault(StringParam, '')
  );
  const [direction, setDirection] = useQueryParam(
    'direction',
    withDefault(StringParam, 'ASC')
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isOpenEditDialogV2, setIsOpenEditDialogV2] = useState(false);
  const [selectedTaskTypeV2, setSelectedTaskTypeV2] = useState<TaskTypeV2>();
  const [{ data: taskTypeList, isLoading, error }, findTaskTypes] =
    useFindTaskTypeList();

  const findTaskTypesWithParams = useCallback(() => {
    findTaskTypes({ page, limit, order, direction, search });
  }, [page, limit, order, direction, search, findTaskTypes]);

  useEffect(() => {
    findTaskTypesWithParams();
  }, [findTaskTypesWithParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Task Types</h2>
        <SearchBox value={search} onSearch={value => setSearch(value)} />
        <PageTemplateHeaderActions>
          <Button
            color='primary'
            variant='contained'
            endIcon={<ArrowDropDownIcon />}
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            Add Task Type
          </Button>
          <Menu
            id='task-type-list-menu'
            open={!!anchorEl}
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>Choose Task Type API</MenuItem>
            <MenuItem
              onClick={() => {
                setIsOpenCreateDialog(true);
                setAnchorEl(null);
              }}
            >
              Create Task Type <StyledWorkflowApiLevelLabel apiLevel={1} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setIsOpenEditDialogV2(true);
                setAnchorEl(null);
              }}
            >
              Create Task Type <StyledWorkflowApiLevelLabel apiLevel={2} />
            </MenuItem>
          </Menu>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell key={column.key}>
                    <TableSortLabel
                      active={order === column.key}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell padding='none' colSpan={columns.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {taskTypeList?.data.map(taskType => (
                <TableRow
                  hover
                  key={`${taskType.id}-${taskType.apiLevel}`}
                  onClick={() => {
                    if (taskType.apiLevel === 2) {
                      // show dialog to edit task type v2
                      setIsOpenEditDialogV2(true);
                      setSelectedTaskTypeV2({
                        id: taskType.id as number,
                        name: taskType.name,
                        label: taskType.label,
                        prefix: taskType.prefix,
                        entityId: taskType.entityId || undefined,
                        entityTable: taskType.entityTable || undefined,
                        modified: taskType.modified
                      });
                      return;
                    }
                    history.push(`/portal/admin/task-types/${taskType.id}`);
                  }}
                >
                  <TableCell>{taskType.name}</TableCell>
                  <TableCell>{taskType.label}</TableCell>
                  <TableCell>{taskType.prefix}</TableCell>
                  <TableCell>{taskType.entity}</TableCell>
                  <TableCell>
                    <WorkflowApiLevelLabel apiLevel={taskType.apiLevel} />
                  </TableCell>
                  <TableCell>{formatDate(taskType.modified)}</TableCell>
                  <TableCell>
                    <UserLink
                      type='name'
                      disableLink={true}
                      user={taskType.modifiedBy}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={taskTypeList?.total || 0}
          rowsPerPage={taskTypeList?.limit || 100}
          page={taskTypeList?.page ? taskTypeList?.page - 1 : 0}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>
      {error && (
        <Alert severity='error'>{`Error occurred. ${error.message}`}</Alert>
      )}
      {isOpenCreateDialog && (
        <TaskTypeCreateDialog
          open={isOpenCreateDialog}
          onCancel={() => setIsOpenCreateDialog(false)}
          onCreated={() => {
            setIsOpenCreateDialog(false);
            findTaskTypesWithParams(); // refresh data
          }}
        />
      )}
      {isOpenEditDialogV2 && (
        <EditTaskTypeDialog
          open
          taskType={selectedTaskTypeV2}
          onCancel={() => {
            setIsOpenEditDialogV2(false);
            setSelectedTaskTypeV2(undefined);
          }}
          onChange={(taskType, created) => {
            findTaskTypesWithParams();
            setIsOpenEditDialogV2(false);
            setSelectedTaskTypeV2(undefined);
            setSnackbar({
              open: true,
              message: `Task type "${taskType.label} (${taskType.prefix})" ${
                created ? 'created' : 'updated'
              } successfully.`
            });
          }}
        />
      )}

      <AppSnackbar
        severity='success'
        open={snackbar.open}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default TaskTypeListPage;
